// src/utils/xmlParser.js
import { arredondar } from "./mathUtils";

/**
 * Lê uma string XML nativa do navegador (DOMParser) e extrai os produtos da NFe ou NFCe.
 *
 * @param {string} xmlString O conteúdo XML importado via FileReader.
 * @returns {object} Objeto contendo o tipo da nota (tpNF: 0 para entrada, 1 para saída) e o array de produtos.
 */
export const parseNotaFiscalXML = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");

    // Verifica se houve erro no parser nativo
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("Erro de formatação no arquivo XML.");
    }

    // 1. Extrair Tipo da Nota (tpNF)
    // Na NFe: <ide><tpNF>0</tpNF></ide> (0=Entrada, 1=Saída)
    const ideNode = xmlDoc.getElementsByTagName("ide")[0];
    if (!ideNode) {
      throw new Error("Arquivo não parece ser uma NFe válida (falta tag <ide>).");
    }

    let tpNF = 0; // Padrão: Entrada
    const tpNFNode = ideNode.getElementsByTagName("tpNF")[0];
    if (tpNFNode && tpNFNode.textContent) {
      tpNF = parseInt(tpNFNode.textContent, 10);
    }

    // Extrair Data de Emissão (dhEmi)
    const dhEmiNode = ideNode.getElementsByTagName("dhEmi")[0];
    const dhEmi = dhEmiNode ? dhEmiNode.textContent : new Date().toISOString();

    // Extrair Valor Total da NF (vNF)
    const totalNode = xmlDoc.getElementsByTagName("total")[0];
    let vNF = 0;
    if (totalNode) {
        const icmsTotNode = totalNode.getElementsByTagName("ICMSTot")[0];
        if (icmsTotNode) {
            const vNFNode = icmsTotNode.getElementsByTagName("vNF")[0];
            if (vNFNode && vNFNode.textContent) {
                vNF = arredondar(parseFloat(vNFNode.textContent));
            }
        }
    }

    // Extrair CNPJ do Emitente (<emit><CNPJ>)
    let cnpjEmitente = "";
    const emitNode = xmlDoc.getElementsByTagName("emit")[0];
    if (emitNode) {
        const cnpjNode = emitNode.getElementsByTagName("CNPJ")[0];
        if (cnpjNode && cnpjNode.textContent) {
            cnpjEmitente = cnpjNode.textContent;
        }
    }

    // Extrair CNPJ/CPF do Destinatário (<dest><CNPJ> ou <dest><CPF>)
    let cnpjDestinatario = "";
    const destNode = xmlDoc.getElementsByTagName("dest")[0];
    if (destNode) {
        const destDocNode = destNode.getElementsByTagName("CNPJ")[0] || destNode.getElementsByTagName("CPF")[0];
        if (destDocNode && destDocNode.textContent) {
            cnpjDestinatario = destDocNode.textContent;
        }
    }

    // 2. Extrair Lista de Produtos (<det>)
    const detNodes = xmlDoc.getElementsByTagName("det");
    const produtos = [];

    for (let i = 0; i < detNodes.length; i++) {
      const prodNode = detNodes[i].getElementsByTagName("prod")[0];
      if (!prodNode) continue;

      // Código do Produto (<cProd>)
      const cProdNode = prodNode.getElementsByTagName("cProd")[0];
      const codigo = cProdNode ? cProdNode.textContent : `ITEM_${i}`;

      // Nome do Produto (<xProd>)
      const xProdNode = prodNode.getElementsByTagName("xProd")[0];
      const nome = xProdNode ? xProdNode.textContent : "Produto sem Nome";

      // Quantidade Comercial (<qCom>)
      const qComNode = prodNode.getElementsByTagName("qCom")[0];
      // A NFe costuma vir com casas decimais: "1.0000". Aplicamos o arredondador.
      const quantidade = qComNode ? arredondar(parseFloat(qComNode.textContent)) : 0;

      // Código de Barras EAN (opcional)
      const cEANNode = prodNode.getElementsByTagName("cEAN")[0];
      const ean = cEANNode && cEANNode.textContent !== "SEM GTIN" ? cEANNode.textContent : "";

      // NCM (opcional, fallback para 'N/A')
      const ncmNode = prodNode.getElementsByTagName("NCM")[0];
      const ncm = ncmNode && ncmNode.textContent ? ncmNode.textContent : "N/A";

      // Valor do Produto (vProd)
      const vProdNode = prodNode.getElementsByTagName("vProd")[0];
      const vProd = vProdNode ? arredondar(parseFloat(vProdNode.textContent)) : 0;

      // Código Fiscal de Operações e Prestações (CFOP)
      const cfopNode = prodNode.getElementsByTagName("CFOP")[0];
      const cfop = cfopNode && cfopNode.textContent ? cfopNode.textContent : "";

      produtos.push({
        codigo,
        nome,
        quantidade,
        ean,
        ncm,
        vProd,
        cfop
      });
    }

    // Extrair Chave de Acesso (chNFe)
    // Tenta pegar da tag chNFe primeiro (que fica dentro do protNFe).
    // Se não achar, tenta pegar do atributo Id da tag infNFe (removendo o "NFe" do começo).
    let chaveAcesso = "";
    const chNFeNode = xmlDoc.getElementsByTagName("chNFe")[0];
    if (chNFeNode && chNFeNode.textContent) {
      chaveAcesso = chNFeNode.textContent;
    } else {
      const infNFeNode = xmlDoc.getElementsByTagName("infNFe")[0];
      if (infNFeNode && infNFeNode.getAttribute("Id")) {
        chaveAcesso = infNFeNode.getAttribute("Id").replace("NFe", "");
      }
    }

    if (!chaveAcesso) {
      throw new Error("Não foi possível encontrar a Chave de Acesso na Nota Fiscal.");
    }

    return {
      success: true,
      tpNF, // 0: Entrada, 1: Saída
      dhEmi,
      vNF,
      chaveAcesso, // Nova prop extraída
      cnpjEmitente, // Utilizado para o mapeamento e validação
      cnpjDestinatario, // Novo campo extraído
      produtos
    };

  } catch (error) {
    console.error("Erro ao fazer parse do XML:", error);
    return {
      success: false,
      error: error.message || "Não foi possível ler o arquivo de Nota Fiscal."
    };
  }
};
